<template lang="pug">
.resident-detail-tab(v-cloak)
  .mb-3
  .d-flex.flex-row.flex-wrap-reverse(v-if="value")
    .box-body.register-body
      .resident-detail-content.d-flex.flex-column
        FormLabel(key-name="利用者ID" :value="value.id")
        FormLabel(key-name="利用者名" :value="value.name")
        FormLabel(key-name="利用者名（ふりがな）" :value="value.kanaName")
        FormLabel(key-name="性別" :value="value.gender")
        FormLabel(key-name="生年月日" :value="getBirthday(value)" )
        FormLabel(key-name="電話番号" :value="value.tel")
        FormLabel(key-name="部屋" :value="convertRoomName(value.roomId)")
        FormLabel(key-name="利用者グループ" :value="convertResidentGroup(value.groupId)")
        FormLabel(key-name="保険者番号" :value="value.insuredNumber")
        FormLabel(key-name="介護保険被保険者番号" :value="value.careInsuranceInsuredNumber")
        .register-row(v-if="value.inAndOutDatetimes" v-for="(item, index) in value.inAndOutDatetimes")
          .register-group
            .register-key
              span.key-name 入所・退所情報{{ index + 1 }}
          .in-and-out-datetime.ml-3
            FormLabel.mb-2(key-name="入所日／入所予定日" :value="convertDateTime(item.inDatetime)")
            FormLabel.mb-2(key-name="入所送迎時間" :value="convertTime(item.pickUpDatetime)")
            FormLabel.mb-2(key-name="退所日／退所予定日" :value="convertDateTime(item.outDatetime)")
            FormLabel.mb-2(key-name="退所送迎時間" :value="convertTime(item.dropOffDatetime)")
            FormLabel.mb-2(key-name="部屋" :value="convertRoomName(item.roomId)")
        FormLabel(key-name="家族ユーザー数" :value="value.familyCount + '人'")
    .buttons.d-flex.flex-row.justify-content-end.flex-fill
      button.btn.edit-btn.mr-2(type="button" @click="gotoResidentEdit") 修正
      button.btn.status-change-btn(v-if="value.waiter" type="button" @click="onChangeStatus") ステップ変更

  //- status change modals
  ChangeStatusModal(v-model="showChangeStatusModal" :title="changeStatusModalTitle"
    :changedStatus.sync="changedStatus" :statusErrorMessage="changeStatusErrorMessage" @ok="confirmChangeStatusModal")
  OkCancelModal(v-model="showChangeStatusConfirmModal" title="ステップを変更しますか？" @ok="changeStatus")
  OkModal(v-model="showChangeStatusSuccessModal" title="変更に成功しました。" @ok="gotoResidentList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState, SendWaiterMessagesState, WaiterListState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";

import ResidentsService, { ResidentGetModel, ResidentStatus, ResidentStatusPatchModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import ChangeStatusModal from "@/pc/containers/residents/ChangeStatusModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * 利用者編集画面コンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    PageLoading,
    FormLabel,
    ChangeStatusModal,
    OkCancelModal,
    OkModal
  }
})
export default class ResidentDetailTab extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 利用者 */
  @Prop()
  value!: ResidentGetModel;

  /** 部屋 */
  rooms: Array<RoomGetModel> = [];
  /** 利用者グループ */
  residentGroups: Array<ResidentGroupsGetModel> = [];

  /** ステップ変更モーダルの表示可否 */
  showChangeStatusModal: boolean = false;
  /** ステップ変更モーダルタイトル */
  changeStatusModalTitle: string = "";
  /** 変更後のステップ */
  changedStatus: ResidentStatus = ResidentStatus.UNKNOWN;
  /** ステップ変更エラーメッセージ */
  changeStatusErrorMessage: string = "";

  /** ステップ変更確認モーダルの表示可否 */
  showChangeStatusConfirmModal: boolean = false;
  /** ステップ変更完了モーダルの表示可否 */
  showChangeStatusSuccessModal: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";
    await this.createView();
    this.isBusy = false;
    return;
  }

  public async createView(): Promise<void> {
    const rooms: Array<RoomGetModel> = await this.getRooms();
    this.rooms = rooms;
    const residentGroups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    this.residentGroups = residentGroups;
    return;
  }

  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /** ### 生年月日を返す */
  getBirthday(resident: ResidentGetModel): string {
    if (resident.birthday) {
      return `${this.convertDate(resident.birthday)}（${this.convertAge(resident.birthday)}歳）`;
    } else {
      return "";
    }
  }

  public convertDateTime(datetime: number): string {
    if (!datetime || isNaN(datetime)) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh：MM", false);
  }

  public convertDate(datetime: number): string {
    if (!datetime || isNaN(datetime)) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日", false);
  }

  public convertAge(datetime: number): string {
    if (!datetime || isNaN(datetime)) {
      return "";
    }
    const date = this.$dateUtil.convertNumberToDate(datetime);
    const age: number = this.$dateUtil.convertBirthdayToAge(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return age.toString();
  }

  public convertTime(datetime: number): string {
    if (!datetime || isNaN(datetime)) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(datetime, "hh：MM");
  }

  public convertRoomName(roomId: number): string {
    for (const room of this.rooms) {
      if (room.id == roomId) {
        return `${room.name}（${this.getUseTypeName(room.useType)}・${this.getRoomTypeName(room.roomType)}）`;
      }
    }
    return "";
  }

  public getUseTypeName(useType: number): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  public getRoomTypeName(roomType: number): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  public convertResidentGroup(groupId: number): string {
    for (const group of this.residentGroups) {
      if (group.id == groupId) {
        return group.name;
      }
    }
    return "";
  }

  public gotoResidentEdit(): void {
    this.$router.push({ name: PcUrl.ResidentEdit, params: { id: `${this.value.id}`, type: "1" } });
    return;
  }

  /**
   * ### ステップ変更モーダルを表示する
   */
  onChangeStatus(): void {
    this.changeStatusModalTitle = `${this.value.name} 様のステップ変更`;
    this.changedStatus = ResidentStatus.UNKNOWN;

    this.showChangeStatusModal = true;
    return;
  }

  /**
   * ### ステップ変更確認モーダルを表示する
   */
  confirmChangeStatusModal(): void {
    this.showChangeStatusConfirmModal = true;
    return;
  }

  async changeStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    this.showChangeStatusModal = false;

    try {
      const requestBody: ResidentStatusPatchModel = this.createUpdateStatusRequestBody();
      await this.residentsService.patchStatus(userStore, this.value.id, requestBody);

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);
      // 待機者一覧を再読み込みさせるようstoreを更新
      let waiterList: WaiterListState = needRefreshesStore.waiterList;
      waiterList.waiterList = true;
      needRefreshesStore.setWaiterList(waiterList);
      // メッセージ送信(待機者)の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

      this.showChangeStatusSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        this.changeStatusErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    }

    this.isBusy = false;

    return;
  }

  createUpdateStatusRequestBody(): ResidentStatusPatchModel {
    return {
      changedStatus: this.changedStatus,
      updated: this.value.updated
    };
  }

  /**
   * ### 利用者一覧画面へ遷移する
   */
  gotoResidentList(): void {
    this.$router.push(PcUrl.ResidentList);
    return;
  }
}
</script>

<style lang="scss" scoped>
.resident-detail-tab {
  position: relative;
  width: 100%;
  min-height: 240px;

  .resident-detail-content {
    max-width: 800px;
    min-width: 640px;
  }

  .buttons {
    width: 325px;
    .edit-btn {
      width: 110px;
    }
  }
}
</style>
